import { TypedDataDomain } from 'ethers'

import { TypedMessageUserRequest } from '../../interfaces/userRequest'

/**
 * ZKX: request-time authorization for the relayer's
 * `/v2/paymaster/:chainId/request` route.
 *
 * That route reserves the sender's gas-tank balance (and burns the fee token's
 * daily-cap headroom) BEFORE anything proves the caller controls
 * `userOperation.sender`. There is no user signature to check at that point:
 * in the 4337 sponsorship flow the userOp is signed AFTER the paymaster answers,
 * because the signed hash covers `paymasterAndData`. So an attacker could POST
 * any funded victim as `sender` and pin their balance until the sweeper
 * reclaims it — a repeatable griefing DoS (no theft: the on-chain EntryPoint
 * still validates the victim's real userOp signature).
 *
 * The fix is a SESSION-scoped EIP-712 authorization, signed once by a key the
 * account is authorized to sign with, cached, and replayed on every paymaster
 * request until it expires. Deliberately NOT bound to a single userOp: binding
 * to `nonce`/`callData` would cost a hardware-wallet confirmation on every
 * single transaction (on top of the 7702 authorization and the userOp itself),
 * while only closing a residual where the holder of a token replays it against
 * their OWN account — self-griefing, already bounded by the relayer's
 * per-account rate limit and in-flight ceiling.
 *
 * Agreed with the backend (2026-09-03):
 *   - domain carries NO `verifyingContract`. The paymaster address is
 *     env-dependent on this side (staging ZKX / prod ZKX / Ambire, see
 *     `AMBIRE_PAYMASTER`) and prod currently proxies to a relayer that knows a
 *     different one — putting it in the domain would 400 every request on the
 *     first env drift. `chainId` already separates chains.
 *   - the backend verifies the RECOVERED signer (== `sender` for 7702,
 *     ∈ `associatedKeys` for a v2 Smart Account). The `key` field the wallet
 *     sends is client-claimed and informational.
 *   - the backend verifies statelessly and enforces
 *     `now < expiresAt <= now + 48h`, so keep the TTL below that.
 *
 * Wire shape (fixed by the relayer's request schema — it is NOT free-form):
 * the signature goes up as the bare hex string `authorization`, and the
 * `expiresAt` it commits to as a sibling `authExpiresAt`. The relayer rebuilds
 * the digest from {account: userOperation.sender, key: the request's `key`,
 * expiresAt: authExpiresAt}, so `key` must carry the ACTUAL signing key — not
 * `associatedKeys[0]`, whose order does not track the signer.
 */

// Kept well under the backend's 48h ceiling, so a session signed just before a
// clock skew still validates.
export const SPONSORSHIP_SESSION_TTL_SECONDS = 24 * 60 * 60

// Treat a session that is about to lapse as already expired: it has to survive
// the paymaster round trip (and its retries) after we hand it over.
export const SPONSORSHIP_SESSION_REFRESH_MARGIN_SECONDS = 5 * 60

export type SponsorshipAuthorization = {
  // the account the sponsorship is requested for (userOperation.sender)
  account: string
  // the key that produced `signature` — informational for the backend, which
  // authorizes on the recovered signer instead
  key: string
  // unix seconds
  expiresAt: number
  signature: string
}

/**
 * Produced by the signing controller (it owns the signer and the
 * hardware-wallet prompt plumbing) and handed to the paymaster, which knows
 * nothing about the keystore.
 */
export type SponsorshipAuthorizer = () => Promise<SponsorshipAuthorization>

export const getSponsorshipAuthorizationTypedData = (
  chainId: bigint,
  account: string,
  key: string,
  expiresAt: number
): TypedMessageUserRequest['meta']['params'] => {
  const domain: TypedDataDomain = {
    name: 'ZKX Gas Tank',
    version: '1',
    chainId: chainId.toString()
  }
  const types = {
    EIP712Domain: [
      { name: 'name', type: 'string' },
      { name: 'version', type: 'string' },
      { name: 'chainId', type: 'uint256' }
    ],
    SponsorshipAuthorization: [
      { name: 'account', type: 'address' },
      { name: 'key', type: 'address' },
      { name: 'expiresAt', type: 'uint256' }
    ]
  }

  return {
    domain,
    types,
    message: { account, key, expiresAt },
    primaryType: 'SponsorshipAuthorization'
  }
}
